/**
 * Optional Sentry bootstrap. No-ops unless VITE_SENTRY_DSN is set.
 * Safe without @sentry/react installed (dynamic import).
 */

type SentryModule = {
  init: (options: {
    dsn: string;
    environment?: string;
    tracesSampleRate?: number;
    sendDefaultPii?: boolean;
  }) => void;
  captureException: (error: unknown) => void;
};

let sentry: SentryModule | null = null;
let initialized = false;
let handlersBound = false;

async function loadSentry(): Promise<SentryModule | null> {
  if (sentry) return sentry;
  try {
    const dynImport = new Function(
      "specifier",
      "return import(specifier)"
    ) as (specifier: string) => Promise<SentryModule>;
    sentry = await dynImport("@sentry/react");
    return sentry;
  } catch {
    return null;
  }
}

function bindGlobalHandlers(): void {
  if (handlersBound || typeof window === "undefined") return;
  handlersBound = true;
  window.addEventListener("error", (event) => {
    captureException(event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason);
  });
}

export async function initSentry(): Promise<void> {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Still bind local handlers so captureException no-ops cleanly
    return;
  }

  const mod = await loadSentry();
  if (!mod) return;

  mod.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  });
  initialized = true;
  bindGlobalHandlers();
}

export function captureException(error: unknown): void {
  if (!initialized) {
    if (import.meta.env.DEV && error) {
      console.warn("[andrum] captureException (Sentry off):", error);
    }
    return;
  }
  void loadSentry().then((mod) => {
    mod?.captureException(error);
  });
}

/** For feature code: report without throwing */
export function reportError(context: string, error: unknown): void {
  if (error instanceof Error) {
    captureException(new Error(`${context}: ${error.message}`, { cause: error }));
    return;
  }
  captureException(new Error(`${context}: ${String(error)}`));
}
